/**
 * Fraud Detection Screen (basic / first-pass)
 *
 * Demonstrates three straightforward, explainable fraud-screening techniques
 * on the invoice data — no ML, no external data:
 *   1. Benford's Law (leading-digit distribution vs. expected)
 *   2. Duplicate invoices (same buyer + amount + date, different document no.)
 *   3. Round-number amounts (fabricated figures cluster on exact thousands)
 *
 * Read-on-demand: reads the filtered analysis pool, so gated on hasMaster.
 */
import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { detectFraud } from "Redux/data/actions";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer,
} from "recharts";

const num = (n) => (n === null || n === undefined || isNaN(n) ? "—" : n.toLocaleString());

// -------------------------------------------------------------
const Fraud = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { fraud } = useSelector((state) => state.data);
  const { hasMaster } = useSelector((state) => state.project);
  const dispatch = useDispatch();

  const run = () => {
    setIsLoading(true);
    dispatch(detectFraud(() => setIsLoading(false)));
  };

  if (!hasMaster)
    return (
      <div className="notification is-warning is-light">
        Build the master data first — fraud screening runs on the invoice pool.
      </div>
    );

  return (
    <div>
      {/* Header + run */}
      <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem" }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "var(--ev-text)" }}>
            Fraud Detection <span style={{ fontSize: "0.7rem", fontWeight: 500, color: "var(--ev-text-muted)" }}>· first-pass screen</span>
          </div>
          <div style={{ fontSize: "0.78rem", color: "var(--ev-text-muted)" }}>
            Three basic, explainable checks on the invoice pool — Benford's Law, duplicate invoices, and round-number amounts.
          </div>
        </div>
        <button
          className={`button is-small ${isLoading ? "is-loading" : ""}`}
          onClick={run}
          disabled={isLoading}
          style={{ backgroundColor: "#79589F", color: "#fff", border: "none", fontWeight: 700, borderRadius: "8px" }}
        >
          <i className="fa-duotone fa-shield-halved" style={{ marginRight: "0.4rem" }} />
          {fraud ? "Re-run Scan" : "Run Fraud Scan"}
        </button>
      </div>

      {isLoading && (
        <div style={{ fontSize: "0.85rem", color: "var(--ev-text-muted)", fontStyle: "italic", padding: "1rem 0" }}>
          Screening invoices...
        </div>
      )}

      {!isLoading && !fraud && (
        <div className="notification is-info is-light" style={{ fontSize: "0.82rem" }}>
          Run the scan to screen the invoice pool for the most common, obvious fraud signatures.
        </div>
      )}

      {!isLoading && fraud && (
        <>
          {/* Headline indicators */}
          <div style={{ fontSize: "0.75rem", color: "var(--ev-text-muted)", marginBottom: "0.5rem" }}>
            {num(fraud.invoices_analysed)} invoices screened · {fraud.flags} of {fraud.indicators.length} checks flagged
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "1.5rem" }}>
            {fraud.indicators.map((ind) => (
              <div key={ind.key} style={{
                flex: "1 1 220px", minWidth: "200px", padding: "0.6rem 0.8rem",
                border: "1px solid var(--ev-border)", borderRadius: "8px",
                borderLeft: `3px solid ${ind.flagged ? "var(--ev-danger)" : "var(--ev-success)"}`,
                backgroundColor: "var(--ev-white)",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                  <i className={`fa-solid ${ind.flagged ? "fa-triangle-exclamation" : "fa-circle-check"}`}
                     style={{ color: ind.flagged ? "var(--ev-danger)" : "var(--ev-success)", fontSize: "0.8rem" }} />
                  <span style={{ fontWeight: 700, fontSize: "0.8rem", color: "var(--ev-text)" }}>{ind.label}</span>
                </div>
                <div style={{ fontSize: "0.72rem", color: "var(--ev-text-muted)", marginTop: "0.2rem" }}>{ind.detail}</div>
              </div>
            ))}
          </div>

          {/* 1. Benford */}
          <Section
            title="1 · Benford's Law"
            blurb="Genuine invoice amounts follow a predictable leading-digit distribution (about 30% start with 1, ~5% with 9). Large deviations can indicate fabricated figures."
          >
            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "center" }}>
              <div style={{ flex: "1 1 380px", minWidth: "320px", height: "260px" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={fraud.benford.digits} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="digit" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} unit="%" />
                    <Tooltip formatter={(v) => `${v}%`} />
                    <Legend wrapperStyle={{ fontSize: "0.72rem" }} />
                    <Bar dataKey="observed_pct" name="Observed" fill="#79589F" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="expected_pct" name="Expected (Benford)" fill="#c9b8de" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div style={{ flex: "0 0 auto" }}>
                <VerdictBadge verdict={fraud.benford.verdict} />
                <div style={{ fontSize: "0.72rem", color: "var(--ev-text-muted)", marginTop: "0.5rem" }}>
                  MAD: <strong>{fraud.benford.mad}</strong><br />
                  n = {num(fraud.benford.n)} amounts
                </div>
              </div>
            </div>
          </Section>

          {/* 2. Duplicates */}
          <Section
            title="2 · Duplicate Invoices"
            blurb="Same buyer, same amount, same issue date — booked under different document numbers. A common signature of double-billing or double-pledging."
          >
            <div style={{ fontSize: "0.82rem", color: "var(--ev-text)", marginBottom: "0.6rem" }}>
              <strong>{num(fraud.duplicates.groups)}</strong> duplicate group(s) · {num(fraud.duplicates.extra_records)} extra record(s)
            </div>
            {fraud.duplicates.examples.length > 0 && (
              <ExampleTable rows={fraud.duplicates.examples} />
            )}
          </Section>

          {/* 3. Round amounts */}
          <Section
            title="3 · Round-Number Amounts"
            blurb={`Fabricated invoices cluster on suspiciously round figures. These are exact multiples of ${num(fraud.round.multiple)}.`}
          >
            <div style={{ fontSize: "0.82rem", color: "var(--ev-text)", marginBottom: "0.6rem" }}>
              <strong>{num(fraud.round.count)}</strong> invoices (<strong>{fraud.round.pct}%</strong>) are exact multiples of {num(fraud.round.multiple)}.
            </div>
            {fraud.round.examples.length > 0 && (
              <ExampleTable rows={fraud.round.examples} />
            )}
          </Section>

          <div style={{ fontSize: "0.7rem", color: "var(--ev-text-muted)", fontStyle: "italic", marginTop: "1rem" }}>
            Basic first-pass screen. Flags are indicators for review, not proof of fraud — genuine data can trip individual checks.
          </div>
        </>
      )}
    </div>
  );
};

// -------------------------------------------------------------
const Section = ({ title, blurb, children }) => (
  <div style={{ marginBottom: "1.5rem" }}>
    <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "var(--ev-text)", marginBottom: "0.2rem" }}>{title}</div>
    <div style={{ fontSize: "0.74rem", color: "var(--ev-text-muted)", marginBottom: "0.6rem", maxWidth: "720px" }}>{blurb}</div>
    {children}
  </div>
);

const VerdictBadge = ({ verdict }) => {
  const good = verdict.startsWith("Close") || verdict.startsWith("Acceptable");
  const tone = good ? "is-success" : verdict.startsWith("Marginal") ? "is-warning" : "is-danger";
  return (
    <span className={`tag ${tone}`} style={{ borderRadius: "6px", fontWeight: 700, fontSize: "0.72rem" }}>
      {verdict}
    </span>
  );
};

const ExampleTable = ({ rows }) => (
  <div className="table-container">
    <table className="table is-fullwidth is-narrow is-striped is-small-table" style={{ fontSize: "0.72rem" }}>
      <thead>
        <tr><th>Buyer</th><th>Document No.</th><th style={{ textAlign: "right" }}>Amount</th><th>Issued</th></tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td>{r.Buyer}</td>
            <td style={{ whiteSpace: "nowrap" }}>{r.Document_Number}</td>
            <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>{r.Amount.toLocaleString()}</td>
            <td style={{ whiteSpace: "nowrap" }}>{r.Issue_Date}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default Fraud;
